import type { AssetEntry } from './campaignDetail/CampaignAssetsEditor';
import { initialState as offsiteInitial, type IOffsiteCampaign } from '../Dashboard/offsiteCampaignCreator.reducer';

/** UGC wizard reducer state — offsite base plus fields added in BrandNewUGCCampaignForm. */
export type UgcCampaignFormDraft = IOffsiteCampaign;

const DRAFT_KEY = 'thspian_brand_ugc_campaign_wizard_draft_v1';

export type SerializableAssetEntry = Pick<AssetEntry, 'url' | 'description' | 'requirements'>;

export interface UgcCampaignWizardDraftPayload {
    step: number;
    form: UgcCampaignFormDraft;
    budgetInput: string;
    publishDate: string;
    participantsInput: string;
    category: string;
    subCategory: string;
    exclusiveRights: boolean;
    featureImagePreview: string | null;
    assets: SerializableAssetEntry[];
    savedAt: string;
}

export function formatUgcDraftSavedAt(date: Date = new Date()): string {
    return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}

export function loadUgcCampaignWizardDraft():
    | (Omit<UgcCampaignWizardDraftPayload, 'savedAt'> & {
          savedAt?: string;
      })
    | null {
    try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as UgcCampaignWizardDraftPayload;
        if (!parsed || typeof parsed !== 'object') return null;
        return {
            step: typeof parsed.step === 'number' ? parsed.step : 0,
            form: { ...offsiteInitial, ...(parsed.form ?? {}) } as UgcCampaignFormDraft,
            budgetInput: parsed.budgetInput ?? '',
            publishDate: parsed.publishDate ?? '',
            participantsInput: parsed.participantsInput ?? '1',
            category: parsed.category ?? '',
            subCategory: parsed.subCategory ?? '',
            exclusiveRights: Boolean(parsed.exclusiveRights),
            featureImagePreview: parsed.featureImagePreview ?? null,
            assets: Array.isArray(parsed.assets) ? parsed.assets : [],
            savedAt: typeof parsed.savedAt === 'string' ? parsed.savedAt : undefined,
        };
    } catch {
        return null;
    }
}

/** Returns ISO `savedAt` when write succeeds; null on quota / storage failure. */
export function saveUgcCampaignWizardDraft(payload: Omit<UgcCampaignWizardDraftPayload, 'savedAt'>): string | null {
    try {
        const assets: SerializableAssetEntry[] = (payload.assets || []).map(({ url, description, requirements }) => ({
            url,
            description,
            requirements,
        }));
        const savedAt = new Date().toISOString();
        const stored: UgcCampaignWizardDraftPayload = {
            ...payload,
            assets,
            savedAt,
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(stored));
        return savedAt;
    } catch {
        return null;
    }
}

export function clearUgcCampaignWizardDraft(): void {
    try {
        localStorage.removeItem(DRAFT_KEY);
    } catch {
        /* ignore */
    }
}
